// Minimal .xlsx writer — enough for "export this table", nothing more.
//
// The panel bundles no spreadsheet library and none is worth adding for one button, but a
// CSV would hand Excel a delimiter/encoding guess it regularly gets wrong on Cyrillic and
// on ids with leading zeros. An .xlsx is a ZIP of a few XML parts, so it is written here
// directly: STORED (uncompressed) entries with a hand-rolled CRC32, and inline strings
// instead of a shared-strings table.
const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let i = 0; i < 256; i++) {
        let c = i;
        for (let k = 0; k < 8; k++)
            c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
        table[i] = c >>> 0;
    }
    return table;
})();
function crc32(data) {
    let c = 0xFFFFFFFF;
    for (let i = 0; i < data.length; i++)
        c = CRC_TABLE[(c ^ data[i]) & 0xFF] ^ (c >>> 8);
    return (c ^ 0xFFFFFFFF) >>> 0;
}
function utf8(s) {
    return new TextEncoder().encode(s);
}
function escXml(s) {
    return s.replace(/[&<>"']/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&apos;'));
}
/**
 * Excel rejects a sheet carrying raw control characters, and the registry's text comes
 * from an app's own screen — strip them rather than produce a file that won't open.
 */
function sanitize(s) {
    // eslint-disable-next-line no-control-regex
    return s.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');
}
/** A1-style column name: 0 → A, 26 → AA. */
function colName(i) {
    let n = i + 1;
    let out = '';
    while (n > 0) {
        const rem = (n - 1) % 26;
        out = String.fromCharCode(65 + rem) + out;
        n = Math.floor((n - 1) / 26);
    }
    return out;
}
/** ZIP container, STORED only — the parts are small and CPU beats pulling in a deflater. */
function zip(entries) {
    const chunks = [];
    const central = [];
    let offset = 0;
    for (const e of entries) {
        const name = utf8(e.name);
        const crc = crc32(e.data);
        const local = new Uint8Array(30 + name.length);
        const dv = new DataView(local.buffer);
        dv.setUint32(0, 0x04034b50, true);
        dv.setUint16(4, 20, true); // version needed
        dv.setUint16(6, 0x0800, true); // UTF-8 names
        dv.setUint16(8, 0, true); // stored
        dv.setUint32(14, crc, true);
        dv.setUint32(18, e.data.length, true);
        dv.setUint32(22, e.data.length, true);
        dv.setUint16(26, name.length, true);
        local.set(name, 30);
        chunks.push(local, e.data);
        const cd = new Uint8Array(46 + name.length);
        const cdv = new DataView(cd.buffer);
        cdv.setUint32(0, 0x02014b50, true);
        cdv.setUint16(4, 20, true);
        cdv.setUint16(6, 20, true);
        cdv.setUint16(8, 0x0800, true);
        cdv.setUint16(10, 0, true);
        cdv.setUint32(16, crc, true);
        cdv.setUint32(20, e.data.length, true);
        cdv.setUint32(24, e.data.length, true);
        cdv.setUint16(28, name.length, true);
        cdv.setUint32(42, offset, true);
        cd.set(name, 46);
        central.push(cd);
        offset += local.length + e.data.length;
    }
    const centralSize = central.reduce((n, c) => n + c.length, 0);
    const end = new Uint8Array(22);
    const edv = new DataView(end.buffer);
    edv.setUint32(0, 0x06054b50, true);
    edv.setUint16(8, entries.length, true);
    edv.setUint16(10, entries.length, true);
    edv.setUint32(12, centralSize, true);
    edv.setUint32(16, offset, true);
    return new Blob([...chunks, ...central, end], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
}
function sheetXml(headers, rows) {
    const cell = (v, r, c, style) => {
        const ref = `${colName(c)}${r}`;
        const st = style ? ` s="${style}"` : '';
        if (typeof v === 'number' && Number.isFinite(v))
            return `<c r="${ref}"${st}><v>${v}</v></c>`;
        const text = sanitize(String(v));
        if (!text)
            return '';
        return `<c r="${ref}"${st} t="inlineStr"><is><t xml:space="preserve">${escXml(text)}</t></is></c>`;
    };
    const head = `<row r="1">${headers.map((h, c) => cell(h, 1, c, '1')).join('')}</row>`;
    const body = rows.map((row, i) => `<row r="${i + 2}">${row.map((v, c) => cell(v, i + 2, c)).join('')}</row>`).join('');
    const width = Math.max(headers.length, 1);
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
<cols><col min="1" max="${width}" width="20" customWidth="1"/></cols>
<sheetData>${head}${body}</sheetData>
<autoFilter ref="A1:${colName(width - 1)}${rows.length + 1}"/>
</worksheet>`;
}
/**
 * One sheet, one header row (bold, frozen), everything else as given. The returned Blob is
 * a complete workbook ready to hand to a download link.
 */
export function buildXlsx(sheetName, headers, rows) {
    const name = sanitize(sheetName).replace(/[\\/*?:[\]]/g, ' ').slice(0, 31) || 'Sheet1';
    const parts = [
        {
            name: '[Content_Types].xml',
            data: utf8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`),
        },
        {
            name: '_rels/.rels',
            data: utf8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`),
        },
        {
            name: 'xl/workbook.xml',
            data: utf8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="${escXml(name)}" sheetId="1" r:id="rId1"/></sheets>
</workbook>`),
        },
        {
            name: 'xl/_rels/workbook.xml.rels',
            data: utf8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`),
        },
        {
            // Two styles only: the default, and s="1" — bold, for the header row.
            name: 'xl/styles.xml',
            data: utf8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="1"><fill><patternFill patternType="none"/></fill></fills>
<borders count="1"><border/></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`),
        },
        { name: 'xl/worksheets/sheet1.xml', data: utf8(sheetXml(headers, rows)) },
    ];
    return zip(parts);
}
/** Hand a built workbook (or any blob) to the browser as a download. */
export function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoked on the next tick: Safari cancels the download if the URL dies synchronously.
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
